// 発注書1本の諸掛（送料・関税・その他）と実原価（landed cost）— parity v2 Step 7。
//
// 🔴 ここで出す実原価は **表示だけ**。商品の原価（cost / 手入力原価）へは書き戻さない
//    ＝同期や再計算で「いつの値か分からない原価」が残らない。文言でもそう説明する。
// 🔴 配賦は **発注数量** で行う（未入荷でも計算できる）。分納で1個あたりが動かない
//    ことを注記する＝黙って安定させない。
// 🔴 開いたときだけ取得する（発注書ごとに1クエリ＝一覧を開いただけで N 本走らせない）。
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  DataTable,
  InlineStack,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  addAdditionalCost,
  deleteAdditionalCost,
  getLandedCost,
  type AdditionalCostKind,
  type AllocationMethod,
} from "../../api/purchase_order_costs";
import { setVariantVolume } from "../../api/variants";
import type { StockyPublicBigIntId } from "../../api/stocky_public_ids";
import {
  getLandedCostPlanPreview,
  planPreviewQueryKey,
} from "../../api/plan_previews";
import { PlanDataPreviewGate } from "../PlanDataPreviewGate";
import { QueryErrorBanner } from "../QueryErrorBanner";

import { t } from "../../lib/i18n";

// 🔴 モジュール直下で t() を呼ばない（initLocale より前に評価され locale が効かない）。
//    生の日本語を持ち、使う側で t() に通す。
const KIND_LABEL: Record<AdditionalCostKind, string> = {
  shipping: "送料・輸送費",
  duty: "関税・輸入諸税",
  other: "その他（検品料・手数料など）",
};

const ALLOCATION_LABEL: Record<AllocationMethod, string> = {
  by_value: "金額比で配分",
  by_qty: "数量比で配分",
  by_volume: "容積比で配分（CBM）",
};

interface Props {
  poId: StockyPublicBigIntId;
  poNumber: string;
}

export function LandedCostPanel({ poId, poNumber }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <BlockStack gap="300">
      <InlineStack align="start">
        <Button
          variant="plain"
          onClick={() => setIsExpanded((open) => !open)}
          ariaExpanded={isExpanded}
          ariaControls={`landed-cost-${poId}`}
        >
          {isExpanded ? t("諸掛と実原価を隠す") : t("諸掛と実原価")}
        </Button>
      </InlineStack>
      {isExpanded && (
        <div id={`landed-cost-${poId}`}>
          <PlanDataPreviewGate
            feature="landed_cost"
            featureName={t("諸掛と実原価")}
            previewQueryKey={planPreviewQueryKey("landed_cost", poId)}
            previewQueryFn={async () => [await getLandedCostPlanPreview(poId)]}
            sections={[
              {
                dataset: "landed_cost",
                title: t("{poNumber} の発注明細", { poNumber }),
                columns: [
                  { key: "product", label: t("商品") },
                  { key: "sku", label: "SKU" },
                  { key: "ordered_qty", label: t("発注数量") },
                  { key: "unit_price", label: t("単価") },
                  { key: "line_amount", label: t("明細金額") },
                  { key: "allocated_amount", label: t("配分された諸掛") },
                  { key: "landed_line_amount", label: t("実原価（明細）") },
                  {
                    key: "landed_unit_cost",
                    label: t("実原価（1個あたり）"),
                  },
                ],
              },
            ]}
          >
            <LandedCostBody poId={poId} poNumber={poNumber} />
          </PlanDataPreviewGate>
        </div>
      )}
    </BlockStack>
  );
}

function LandedCostBody({ poId, poNumber }: Props) {
  const queryClient = useQueryClient();
  const queryKey = ["landed-cost", poId];

  const [kind, setKind] = useState<AdditionalCostKind>("shipping");
  const [allocation, setAllocation] = useState<AllocationMethod>("by_value");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => getLandedCost(poId),
  });

  const addMutation = useMutation({
    mutationFn: () =>
      addAdditionalCost(poId, {
        kind,
        amount: amount.trim(),
        allocation,
        note: note.trim() === "" ? null : note.trim(),
      }),
    onSuccess: (view) => {
      queryClient.setQueryData(queryKey, view);
      setAmount("");
      setNote("");
      setFormError(null);
    },
    onError: (error: Error) => setFormError(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (costId: string) => deleteAdditionalCost(poId, costId),
    onSuccess: (view) => queryClient.setQueryData(queryKey, view),
    onError: (error: Error) => setFormError(error.message),
  });

  if (isLoading) return <SkeletonBodyText lines={3} />;
  if (isError || !data) {
    return <QueryErrorBanner title={t("諸掛")} onRetry={() => void refetch()} />;
  }

  // 金額は数値として妥当なときだけ送る（サーバの 422 を待たずに手前で止める）。
  const parsedAmount = Number(amount.trim());
  const canSubmit =
    amount.trim() !== "" && Number.isFinite(parsedAmount) && parsedAmount > 0;

  return (
    <BlockStack gap="400">
      <Text as="h3" variant="headingSm">
        {t("{poNumber} の諸掛と実原価", { poNumber })}
      </Text>

      {formError && (
        <Banner tone="critical" onDismiss={() => setFormError(null)}>
          {formError}
        </Banner>
      )}

      {data.unallocatable_reason && (
        <Banner tone="warning" title={t("諸掛を配分できません")}>
          <Text as="p">{data.unallocatable_reason}</Text>
          <Text as="p" variant="bodySm">
            {t("下の一覧から配分方法を変えるか、その諸掛を削除してください。")}
          </Text>
        </Banner>
      )}

      <InlineStack gap="300" blockAlign="end" wrap>
        <div style={{ minWidth: 200 }}>
          <Select
            label={t("種類")}
            options={(Object.keys(KIND_LABEL) as AdditionalCostKind[]).map((value) => ({
              label: t(KIND_LABEL[value]),
              value,
            }))}
            value={kind}
            onChange={(value) => setKind(value as AdditionalCostKind)}
          />
        </div>
        <div style={{ width: 140 }}>
          <TextField
            label={t("金額")}
            autoComplete="off"
            type="number"
            min={1}
            value={amount}
            onChange={setAmount}
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select
            label={t("配分方法")}
            options={(Object.keys(ALLOCATION_LABEL) as AllocationMethod[]).map((value) => ({
              label: t(ALLOCATION_LABEL[value]),
              value,
            }))}
            value={allocation}
            onChange={(value) => setAllocation(value as AllocationMethod)}
          />
        </div>
        <div style={{ minWidth: 200, flex: 1 }}>
          <TextField
            label={t("メモ（任意）")}
            autoComplete="off"
            value={note}
            onChange={setNote}
          />
        </div>
        <Button
          variant="primary"
          onClick={() => addMutation.mutate()}
          disabled={!canSubmit || addMutation.isPending}
          loading={addMutation.isPending}
        >
          {t("諸掛を追加")}
        </Button>
      </InlineStack>

      {data.costs.length === 0 ? (
        <Text as="p" variant="bodySm" tone="subdued">
          {t("諸掛はまだ登録されていません。送料や関税を足すと、明細ごとの実原価を計算します。")}
        </Text>
      ) : (
        <BlockStack gap="200">
          {data.costs.map((cost) => (
            <InlineStack key={cost.id} gap="300" blockAlign="center" wrap={false}>
              <Badge>{t(KIND_LABEL[cost.kind])}</Badge>
              <Text as="span">{cost.amount}</Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {t(ALLOCATION_LABEL[cost.allocation])}
              </Text>
              {cost.note && (
                <Text as="span" variant="bodySm" tone="subdued">
                  {cost.note}
                </Text>
              )}
              <Button
                variant="plain"
                tone="critical"
                onClick={() => deleteMutation.mutate(cost.id)}
                disabled={deleteMutation.isPending}
              >
                {t("削除")}
              </Button>
            </InlineStack>
          ))}
        </BlockStack>
      )}

      <InlineStack gap="500" wrap>
        <Text as="span" variant="bodySm">
          {t("商品計")}: {data.goods_total}
        </Text>
        <Text as="span" variant="bodySm">
          {t("諸掛計")}: {data.additional_cost_total}
        </Text>
        <Text as="span" variant="bodySm" fontWeight="semibold">
          {t("実原価合計")}: {data.landed_total ?? t("計算できません")}
        </Text>
      </InlineStack>

      {data.lines.length > 0 && (
        <DataTable
          columnContentTypes={[
            "text",
            "numeric",
            "numeric",
            "numeric",
            "numeric",
            "numeric",
          ]}
          headings={[
            t("商品"),
            t("発注数量"),
            t("容積（m³/個）"),
            t("明細金額"),
            t("配分された諸掛"),
            t("実原価（1個あたり）"),
          ]}
          rows={data.lines.map((line) => [
            line.title ?? line.sku ?? t("（商品名なし）"),
            line.ordered_qty.toLocaleString(),
            // 🔴 variant が消えた明細は容積を持てない（入力先が無い）＝ダッシュ表示。
            line.variant_id !== null ? (
              <VolumeCell
                // サーバ値が変わったら入力欄ごと作り直す＝手元の編集中の値と
                // サーバ値の二重管理をしない（保存後は必ずサーバ値に揃う）。
                key={`${line.item_id}:${line.unit_volume_m3 ?? ""}`}
                variantId={line.variant_id}
                value={line.unit_volume_m3}
                label={t("{item} の容積（m³）", {
                  item: line.sku ?? line.item_id,
                })}
                onError={setFormError}
              />
            ) : (
              "—"
            ),
            line.line_amount,
            line.allocated_amount,
            line.landed_unit_cost ?? "—",
          ])}
        />
      )}

      <Text as="p" variant="bodySm" tone="subdued">
        {t("実原価は表示のみで、商品の原価には書き込みません。配分は発注数量で行うため、分納しても1個あたりの実原価は変わりません。")}
        {t("容積は1個あたりの m³（CBM）で入力します。容積比で配分するには、すべての明細に容積が必要です（空欄の明細があると配分できません）。")}
      </Text>
    </BlockStack>
  );
}

interface VolumeCellProps {
  variantId: string;
  /** サーバの現在値（"0.015" など）。null＝未入力。 */
  value: string | null;
  label: string;
  onError: (message: string) => void;
}

/**
 * 明細1行の容積（m³/個）を欄外に出ずにその場で直す入力欄。
 *
 * - 確定はフォーカスを外したとき（blur）＝打鍵ごとに PATCH を飛ばさない。
 * - 空欄で確定＝「未入力」へ戻す（null）。0 は「容積ゼロ」として別の意味を持つ。
 * - 保存後は landed-cost クエリを丸ごと無効化する＝同じ商品が他の発注書にも
 *   載っている（容積は variant の属性で発注書のものではない）。
 */
function VolumeCell({ variantId, value, label, onError }: VolumeCellProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(value ?? "");

  const mutation = useMutation({
    mutationFn: (next: string | null) => setVariantVolume(variantId, next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landed-cost"] });
    },
    onError: (error: Error) => {
      setDraft(value ?? "");
      onError(error.message);
    },
  });

  const commit = () => {
    const trimmed = draft.trim();
    const current = value ?? "";
    if (trimmed === current) return;
    if (trimmed === "") {
      mutation.mutate(null);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      // 読めない値は送らない＝黙って捨てず、元の値へ戻して理由を出す。
      setDraft(current);
      onError(t("容積は0以上の数値で入力してください（m³）。"));
      return;
    }
    mutation.mutate(trimmed);
  };

  return (
    <div style={{ width: 110 }}>
      <TextField
        label={label}
        labelHidden
        autoComplete="off"
        type="number"
        min={0}
        step={0.001}
        placeholder={t("未入力")}
        value={draft}
        onChange={setDraft}
        onBlur={commit}
        disabled={mutation.isPending}
      />
    </div>
  );
}
